/* Benachrichtigungstypen und ihre Zuordnung.
 *
 * Reine Funktionen, kein DOM. Genutzt von der Glocke (NotificationBell.vue),
 * vom Auslösen nach dem Speichern (usePlayerEvents.js) und in den Tests.
 *
 * Jedes Ereignis hat einen EIGENEN Typ, damit sich später jedes einzeln für
 * den Push ein- und ausschalten lässt (Einstellungen je Nutzer). Insbesondere
 * getrennt sind Newsfeed (`feed_new`) und Trial-Update (`trial_update`) — die
 * alte Fassung führte beide unter `player_update`.
 *
 * Für diese Typen speichert die Datenbank KEINEN fertigen Text: `title` ist
 * der Spielername, `body` bleibt leer. Den Text "News: <Thema>" baut die
 * Glocke aus den Sprachdateien (notifications.types.<typ>) in der Sprache des
 * Betrachters, die Edge Function notify baut den Push-Text in der Sprache des
 * Empfängers. Zeilen mit anderem Typ — vor allem die alten `player_update` —
 * werden unverändert mit `title`/`body` angezeigt.
 */

/** Alle Typen, die die App auslöst. Reihenfolge = Reihenfolge in den Einstellungen. */
export const NOTIFICATION_TYPES = [
  'feed_new',       // Neuer Newsfeed-Eintrag
  'visa_new',       // Neues Visum
  'visa_status',    // Visum-Statuswechsel
  'trip_new',       // Neue Reise / Trial
  'trial_update',   // Neues Update an einer Reise / einem Trial
  'invitation_new', // Neue Einladung
  'status_change',  // Spielerstatus geändert (camp, trial, injured …)
];

/**
 * Typen, die nur Admins bekommen. 'access_request' = neue Zugangsanfrage; sie
 * kommt nicht aus der App, sondern aus einem Datenbank-Trigger auf
 * access_requests (supabase/zugangsanfrage-erweitert.sql). Gleiche Rolle wie in
 * TYPE_ROLES (supabase/functions/notify/recipients.ts) und in der Policy
 * notifications_select — beim Ändern alle drei anfassen.
 * Bewusst NICHT in NOTIFICATION_TYPES: Das sind die Spielerereignisse, die die
 * App auslöst.
 */
export const ADMIN_ONLY_TYPES = ['access_request'];

/** Alle Typen, deren Text aus den Sprachdateien kommt. */
export const ALL_NOTIFICATION_TYPES = [...NOTIFICATION_TYPES, ...ADMIN_ONLY_TYPES];

/** Typen, die in den Push-Einstellungen dieser Rolle erscheinen (für Admins zusätzlich die Admin-Typen). */
export function settingsTypesFor(role) {
  return role === 'admin' ? ALL_NOTIFICATION_TYPES : NOTIFICATION_TYPES;
}

const TYPE_TAB = {
  visa_new: 'travel',
  visa_status: 'travel',
  trip_new: 'travel',
  trial_update: 'travel',
  invitation_new: 'travel',
  // feed_new, status_change: kein eigener Reiter (Newsfeed-Reiter ist noch offen,
  // siehe TODO.md) — der Spieler öffnet auf seinem Standard-Reiter.
};

/**
 * @param {string} type  n.type aus der `notifications`-Tabelle
 * @returns {string|null} Reiter-Schlüssel für PlayerPanel.vue, oder null ohne
 *   eindeutige Zuordnung. Alte Zeilen (`player_update`) liefern null.
 */
export function tabForNotificationType(type) {
  return TYPE_TAB[type] || null;
}

/**
 * Rollen, die Benachrichtigungen (und damit Push und Push-Einstellungen)
 * bekommen. Gleiche Liste wie die Policy notifications_select und PUSH_ROLES in
 * supabase/functions/notify/recipients.ts — beim Ändern alle drei anfassen.
 * Ordnung, nicht Sicherheit: durchgesetzt wird es in Datenbank und Funktion.
 */
export const PUSH_ROLES = ['admin', 'board', 'data'];

export function receivesPush(role) {
  return PUSH_ROLES.includes(role);
}

/**
 * Neue Liste ausgeschalteter Typen, wenn `type` ein- oder ausgeschaltet wird.
 * Unbekannte Einträge (z. B. Typen einer neueren App-Fassung) bleiben erhalten
 * und werden nicht stillschweigend gelöscht. Keine Doppelten, Reihenfolge wie
 * NOTIFICATION_TYPES, unbekannte danach.
 */
export function withTypeEnabled(disabledTypes, type, enabled) {
  const set = new Set(Array.isArray(disabledTypes) ? disabledTypes : []);
  if (enabled) set.delete(type);
  else set.add(type);
  const known = ALL_NOTIFICATION_TYPES.filter((t) => set.has(t));
  const unknown = [...set].filter((t) => !ALL_NOTIFICATION_TYPES.includes(t));
  return [...known, ...unknown];
}

/** Ist der Push für diesen Typ an? Kein Eintrag = an. */
export function isTypeEnabled(disabledTypes, type) {
  return !(Array.isArray(disabledTypes) ? disabledTypes : []).includes(type);
}

/** Ist das ein Typ, dessen Text aus den Sprachdateien gebaut wird? */
export function isLocalizedType(type) {
  return ALL_NOTIFICATION_TYPES.includes(type);
}

/**
 * i18n-Schlüssel für den Text einer Meldung, oder null, wenn die Zeile ihren
 * Text selbst mitbringt (alter Typ).
 */
export function notificationTextKey(type) {
  return isLocalizedType(type) ? `notifications.types.${type}` : null;
}

/**
 * push_preferences.language lässt in der Datenbank nur 'de'/'en' zu (Sperre
 * in supabase/push-preferences.sql) — die Edge Function notify texted den
 * Push bislang nur in diesen beiden Sprachen. Mit Französisch/Spanisch als
 * App-Sprache dazugekommen: beide fallen für den Push auf 'en' zurück, statt
 * einen von der Datenbank abgelehnten Wert zu schreiben (würde die Sperre
 * verletzen und beim Speichern scheitern).
 */
export const PUSH_LANGUAGES = ['de', 'en'];

/** Welche push_preferences.language-Wert für diese App-Sprache geschrieben wird. */
export function pushLanguageFor(locale) {
  return PUSH_LANGUAGES.includes(locale) ? locale : 'en';
}
